import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
// import { NouiFormatter, NouisliderComponent } from 'nouislider';
import { Options } from '@angular-slider/ngx-slider';
import {
  DEFAULT_FLIGHT_FILTERS,
  FlightEnterpriseSearchInterface,
  FlightFilters,
  FlightSortTypes,
  FlightDisplayTypes,
  FlightSearchType
} from '@sabstravtech/obtservices/base';
import {
  ServiceType,
  EnterpriseSearchService,
  WithSubscriptionComponent,
  UserService
} from '@sabstravtech/obtservices/angular';
import { Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';

import { Helpers } from '../../../../core/classes/helpers';
import { LightningUserFavorurite } from '../../../../core/classes/user-favourite.enum';

interface RangeFilterValue {
  value: number;
  highValue: number;
}

interface FilterValue {
  key: string;
  title: string;
  checked: boolean;
}

@Component({
  selector: 'app-flight-sort-by',
  templateUrl: './flight-sort-by.component.html',
  styleUrls: ['./flight-sort-by.component.scss'],
  standalone: false
})
export class FlightSortBy extends WithSubscriptionComponent implements OnInit, OnChanges {
  @Input() filters: FlightFilters;
  @Input() display: FlightDisplayTypes;
  public ourFilters: FlightFilters = DEFAULT_FLIGHT_FILTERS;
  public priceOptions: Options = { floor: 0, ceil: 0 };
  public depTimeOptions: Options = { floor: 0, ceil: 0 };
  public arrTimeOptions: Options = { floor: 0, ceil: 0 };

  public sortType = FlightSortTypes.Cheapest;
  public searchParams: FlightEnterpriseSearchInterface;
  public FlightSearchType = FlightSearchType;
  public selectableFilters;
  public standardFilters = [
    {
      title: 'Preferred',
      sortType: FlightSortTypes.Preferred,
      icon: 'ico-heart',
      ariaLabel: 'Sort by Preferred'
    },
    {
      title: 'Price',
      sortType: FlightSortTypes.Cheapest,
      icon: 'ico-gbp',
      ariaLabel: 'Sort by Price'
    },
    {
      title: 'Lowest Emissions',
      sortType: FlightSortTypes.LowestEmissions,
      icon: 'ico-co2',
      ariaLabel: 'Sort by Co2 Emissions'
    },
    {
      title: 'Quickest',
      sortType: FlightSortTypes.Quickest,
      icon: 'ico-clock',
      ariaLabel: 'Sort by Quickest'
    },
    {
      title: 'Most Direct',
      sortType: FlightSortTypes.MostDirect,
      icon: 'ico-chevron-right',
      ariaLabel: 'Sort by Most Direct'
    },
    {
      title: 'Departure Time',
      sortType: FlightSortTypes.DepartureTime,
      icon: 'ico-icon-takeoff',
      ariaLabel: 'Sort by Departure Time'
    },
    {
      title: 'Arrival Time',
      sortType: FlightSortTypes.ArrivalTime,
      icon: 'ico-icon-landing',
      ariaLabel: 'Sort by Arrival Time'
    }
  ];

  public $triggerApplyFilter = new Subject<FlightFilters>();

  constructor(
    public searchService: EnterpriseSearchService,
    private userService: UserService
  ) {
    super();
    this.searchParams = this.searchService.searches[ServiceType.Flight];
  }

  ngOnInit() {
    if (this.searchParams.bySchedule) {
      const priceFilterIndex = this.standardFilters.findIndex((filter) => filter.title === 'Price');
      if (priceFilterIndex !== -1) {
        this.standardFilters.splice(priceFilterIndex, 1);
      }
    }
    this.selectableFilters = this.standardFilters;
    const defaultSortType = this.searchParams.bySchedule
      ? FlightSortTypes.Quickest
      : FlightSortTypes.Preferred;
    const userPreference = this.userService.getUserFavoriteObject<{ sortOrder: FlightSortTypes }>(
      LightningUserFavorurite.FlightResultsConfiguration
    )?.sortOrder;

    if (this.searchParams.bySchedule && userPreference === FlightSortTypes.Cheapest) {
      this.sortType = defaultSortType;
    } else {
      this.sortType = userPreference || defaultSortType;
    }

    this.subscribe(this.$triggerApplyFilter.pipe(debounceTime(200)), (filters: FlightFilters) => {
      this.searchParams.applyFilters(filters, this.sortType);
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['filters']) {
      this.ourFilters = this.clearFlightFilters(this.filters);
      if (this.searchParams.bySchedule || changes.display) {
        const defaultSortType = this.searchParams.bySchedule
          ? FlightSortTypes.Quickest
          : FlightSortTypes.Cheapest;
        const userPreference = this.userService.getUserFavoriteObject<{
          sortOrder: FlightSortTypes;
        }>(LightningUserFavorurite.FlightResultsConfiguration)?.sortOrder;

        if (this.searchParams.bySchedule && userPreference === FlightSortTypes.Cheapest) {
          this.sortType = defaultSortType;
        } else {
          this.sortType = userPreference || defaultSortType;
        }
        this.searchParams.applyFilters(this.filters, this.sortType);
      }
    }
  }

  clear(): void {
    this.ourFilters = this.clearFlightFilters(this.filters);
    this.applyFilter(this.ourFilters);
  }

  clearFlightFilters(original: FlightFilters): FlightFilters {
    if (!original) {
      return DEFAULT_FLIGHT_FILTERS;
    }
    const filters = Helpers.clone(original);
    this.priceOptions = {
      floor: Math.floor(filters.price.min === Number.MAX_SAFE_INTEGER ? 0 : filters.price.min),
      ceil: Math.ceil(filters.price.max === Number.MIN_SAFE_INTEGER ? 0 : filters.price.max),
      step: 1
    };
    filters.price.min = this.priceOptions.floor;
    filters.price.max = this.priceOptions.ceil;
    this.depTimeOptions = {
      floor: filters.departureTime.min,
      ceil: filters.departureTime.max
    };
    this.arrTimeOptions = {
      floor: filters.arrivalTime.min,
      ceil: filters.arrivalTime.max
    };

    return filters;
  }

  applyFilter(filters: FlightFilters) {
    this.$triggerApplyFilter.next(filters);
  }

  setSortby(sortType: FlightSortTypes): void {
    this.sortType = sortType;
    this.applyFilter(this.ourFilters);
  }
}
